import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import { ReactComponent as CloseIcon } from "../assets/icons/close.svg";
import { usePromotedBottomPoll, usePollPopup } from "./api/pollsV2";
import { useUser, ACCESS_LEVELS } from "../services/hooks/useUser";
import "./SurveyBottomBar.scss";

/**
 * SurveyBottomBar component displays a fixed bottom bar promoting a survey
 * Automatically fetches and displays the promoted bottom poll from the API
 *
 * @param {Object} props
 * @param {string} props.className - Additional CSS class name
 */
const SurveyBottomBar = ({ className }) => {
  const { i18n, t } = useTranslation(["survey"]);
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const { isLoggedIn, isBnppClient } = useUser();

  // Don't show bottom bar on survey/poll pages
  const currentPath = window.location.pathname;
  const isOnSurveyPage =
    currentPath.includes("/ankiety/") || currentPath.includes("/surveys/");

  // Get language code from cookies or i18n
  const userLang = Cookies.get("user_lang");
  const langCode = userLang
    ? userLang.toUpperCase()
    : i18n.language.toUpperCase();

  // Fetch the promoted bottom poll
  const { poll, isLoading, isError } = usePromotedBottomPoll(langCode);

  // Check if poll was previously dismissed
  useEffect(() => {
    if (poll && poll.id) {
      const dismissedPollsCookie = Cookies.get("dismissedBottomPolls");
      const dismissedPolls = dismissedPollsCookie
        ? JSON.parse(dismissedPollsCookie)
        : [];
      setIsDismissed(dismissedPolls.includes(poll.id));
    }
  }, [poll && poll.id]);

  const promotedPopupID = poll
    ? poll.promoted_popup
      ? poll.promoted_popup.id
      : null
    : null;

  const { popup } = usePollPopup(promotedPopupID);

  // Show bar when poll is loaded and not dismissed
  useEffect(() => {
    if (poll && !isDismissed && !isLoading && !isError && !isOnSurveyPage) {
      if (poll.publish_status !== 3) {
        setIsVisible(false);
        return;
      }

      // Check access level restrictions
      if (poll.access === ACCESS_LEVELS.BNPP_CLIENT && !isBnppClient) {
        setIsVisible(false);
        return;
      }

      if (poll.access === ACCESS_LEVELS.LOGGED && !isLoggedIn) {
        setIsVisible(false);
        return;
      }

      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [
    poll,
    isDismissed,
    isLoading,
    isError,
    isOnSurveyPage,
    isLoggedIn,
    isBnppClient,
  ]);

  const handleClose = () => {
    if (poll && poll.id) {
      // Save dismissed poll ID to cookie (expires in 1 hour)
      const dismissedPollsCookie = Cookies.get("dismissedBottomPolls");
      const dismissedPolls = dismissedPollsCookie
        ? JSON.parse(dismissedPollsCookie)
        : [];
      if (!dismissedPolls.includes(poll.id)) {
        dismissedPolls.push(poll.id);
        Cookies.set("dismissedBottomPolls", JSON.stringify(dismissedPolls), {
          expires: 1 / 24, // 1 hour (1/24 of a day)
        });
      }
    }
    setIsVisible(false);
    setIsDismissed(true);
  };

  const handleCtaClick = () => {
    if (poll && poll.slug) {
      // Navigate to poll page
      window.location.href = `/ankiety/${poll.slug}`;
    }
  };

  if (!poll || !popup || isOnSurveyPage) {
    return null;
  }

  if (poll.publish_status !== 3) {
    return null;
  }

  if (poll.access === ACCESS_LEVELS.BNPP_CLIENT && !isBnppClient) {
    return null;
  }

  if (poll.access === ACCESS_LEVELS.LOGGED && !isLoggedIn) {
    return null;
  }

  // Don't render if not visible or no poll data
  if (!isVisible) {
    return (
      <div className="survey-bottom-bar-button">
        <button
          type="button"
          className="survey-bottom-bar-button__show"
          onClick={() => setIsVisible(true)}
          aria-label="Show Survey Bottom Bar"
        >
          {t("survey:wizard.fillSurvey")}
          <span>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20.25 4.5V9C20.25 9.19891 20.171 9.38968 20.0303 9.53033C19.8897 9.67098 19.6989 9.75 19.5 9.75C19.3011 9.75 19.1103 9.67098 18.9697 9.53033C18.829 9.38968 18.75 9.19891 18.75 9V6.31031L14.0306 11.0306C13.8899 11.1714 13.699 11.2504 13.5 11.2504C13.301 11.2504 13.1101 11.1714 12.9694 11.0306C12.8286 10.8899 12.7496 10.699 12.7496 10.5C12.7496 10.301 12.8286 10.1101 12.9694 9.96937L17.6897 5.25H15C14.8011 5.25 14.6103 5.17098 14.4697 5.03033C14.329 4.88968 14.25 4.69891 14.25 4.5C14.25 4.30109 14.329 4.11032 14.4697 3.96967C14.6103 3.82902 14.8011 3.75 15 3.75H19.5C19.6989 3.75 19.8897 3.82902 20.0303 3.96967C20.171 4.11032 20.25 4.30109 20.25 4.5ZM9.96937 12.9694L5.25 17.6897V15C5.25 14.8011 5.17098 14.6103 5.03033 14.4697C4.88968 14.329 4.69891 14.25 4.5 14.25C4.30109 14.25 4.11032 14.329 3.96967 14.4697C3.82902 14.6103 3.75 14.8011 3.75 15V19.5C3.75 19.6989 3.82902 19.8897 3.96967 20.0303C4.11032 20.171 4.30109 20.25 4.5 20.25H9C9.19891 20.25 9.38968 20.171 9.53033 20.0303C9.67098 19.8897 9.75 19.6989 9.75 19.5C9.75 19.3011 9.67098 19.1103 9.53033 18.9697C9.38968 18.829 9.19891 18.75 9 18.75H6.31031L11.0306 14.0306C11.1714 13.8899 11.2504 13.699 11.2504 13.5C11.2504 13.301 11.1714 13.1101 11.0306 12.9694C10.8899 12.8286 10.699 12.7496 10.5 12.7496C10.301 12.7496 10.1101 12.8286 9.96937 12.9694Z"
                fill="white"
              />
            </svg>
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className={`survey-bottom-bar ${className || ""}`}>
      {/* Header with Title and Close Button */}
      <div className="survey-bottom-bar__header">
        <p className="survey-bottom-bar__title">
          {popup.headline || poll.title}
        </p>
        <button
          type="button"
          className="survey-bottom-bar__close-button"
          onClick={handleClose}
          aria-label="Close"
        >
          <CloseIcon />
        </button>
      </div>

      {/* Content Section */}
      <div className="survey-bottom-bar__content">
        {/* Description */}
        <div className="survey-bottom-bar__description-container">
          <div
            className="survey-bottom-bar__description"
            dangerouslySetInnerHTML={{
              __html: popup.content || poll.description,
            }}
          />
        </div>

        {/* CTA Button */}
        <button
          type="button"
          className="survey-bottom-bar__cta-button"
          onClick={handleCtaClick}
        >
          <span className="survey-bottom-bar__cta-text">
            {popup && popup.cta_text ? popup.cta_text : "Wypełnij ankietę"}
          </span>
        </button>
      </div>
    </div>
  );
};

SurveyBottomBar.propTypes = {
  className: PropTypes.string,
};

SurveyBottomBar.defaultProps = {
  className: "",
};

export default SurveyBottomBar;
